import React from 'react';
import { Gift, Users, UserX, UserPlus, Search, Trophy } from 'lucide-react';
import { useBranding } from '../../../context/BrandingContext';

const TIER_KEYS = ['bronze', 'silver', 'gold', 'platinum'];
const SEGMENTS = [
  { value: '', label: 'Tutti i clienti', icon: Users },
  { value: 'inactive', label: 'Clienti inattivi', icon: UserX },
  { value: 'new', label: 'Nuovi clienti', icon: UserPlus },
  { value: 'top_month', label: 'Top del mese', icon: Trophy },
];

// Pure UI for the gift-target selector. Owns NO state — driven entirely
// by the `useGiftTarget()` hook so the two tabs (Punti / Coupon) share
// the exact same selection criteria.
export function TargetSelector({ ctrl }) {
  const { state, setters, actions } = ctrl;
  const { mode, selectedTiers, segment, inactiveDays, newDays,
          searchQuery, searchResult, suggestions, showSuggestions } = state;
  const { setMode, setSegment, setInactiveDays, setNewDays, setSearchQuery, setShowSuggestions } = setters;
  const { selectCustomer, clearSelection, toggleTier } = actions;
  const { tier_names } = useBranding();
  const tierLabel = (k) => tier_names?.[k] || (k ? k[0].toUpperCase() + k.slice(1) : '—');
  const TIERS = TIER_KEYS.map(k => ({ value: k, label: tierLabel(k) }));

  return (
    <>
      <div className="flex gap-2 mb-6">
        <button onClick={() => setMode('segment')} data-testid="mode-segment"
          className={`flex-1 h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border transition-all ${
            mode === 'segment' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200'}`}>
          <Users size={16} /> Gruppo / Segmento
        </button>
        <button onClick={() => setMode('single')} data-testid="mode-single"
          className={`flex-1 h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 border transition-all ${
            mode === 'single' ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200'}`}>
          <Gift size={16} /> Singolo cliente
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-4">
        {mode === 'single' ? (
          <div>
            <label className="text-xs text-[#64748B] font-medium block mb-2">Cerca cliente per nome o telefono</label>
            <div className="relative">
              <input type="text" value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); if (searchResult) clearSelection(); }}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Es. Marco Bianchi o 339..."
                data-testid="gift-search-input"
                className="w-full h-11 bg-slate-50 border border-slate-200 rounded-xl px-4 pr-10 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none"
                autoComplete="off" />
              <Search size={16} className="absolute right-3 top-3.5 text-[#64748B]" />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl overflow-hidden z-20 shadow-lg" data-testid="gift-suggestions-dropdown">
                  {suggestions.map(c => (
                    <button key={c.id} onClick={() => selectCustomer(c)}
                      data-testid={`gift-suggestion-${c.id}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-[#D44E20]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#D44E20] font-bold text-xs">{c.name?.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1A1A1A] text-sm font-medium truncate">{c.name}</p>
                        <p className="text-[#64748B] text-xs">{c.phone}</p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className="text-[#D44E20] text-xs font-bold">{c.points} pt</span>
                        <p className="text-[#64748B] text-[10px] uppercase">{tierLabel(c.tier)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {searchResult && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3" data-testid="gift-search-result">
                <div className="w-10 h-10 bg-[#D44E20] rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">{searchResult.name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A1A1A]">{searchResult.name}</p>
                  <p className="text-xs text-[#64748B]">{searchResult.phone} — {searchResult.points} pt — {tierLabel(searchResult.tier)}</p>
                </div>
                <button onClick={clearSelection} className="ml-auto text-[#64748B] hover:text-[#D44E20] text-lg">&times;</button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#64748B] font-medium block mb-2">Segmento</label>
              <div className="flex gap-2 flex-wrap">
                {SEGMENTS.map(s => (
                  <button key={s.value} onClick={() => setSegment(s.value)}
                    data-testid={`gift-segment-${s.value || 'all'}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                      segment === s.value ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'}`}>
                    <s.icon size={13} /> {s.label}
                  </button>
                ))}
              </div>
              {segment === 'inactive' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-[#64748B]">Inattivi da</span>
                  <input type="number" value={inactiveDays} onChange={e => setInactiveDays(e.target.value)}
                    className="w-16 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-center" />
                  <span className="text-xs text-[#64748B]">giorni</span>
                </div>
              )}
              {segment === 'new' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-[#64748B]">Iscritti negli ultimi</span>
                  <input type="number" value={newDays} onChange={e => setNewDays(e.target.value)}
                    className="w-16 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-center" />
                  <span className="text-xs text-[#64748B]">giorni</span>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-[#64748B] font-medium block mb-2">Tier (opzionale)</label>
              <div className="flex gap-2">
                {TIERS.map(t => (
                  <button key={t.value} onClick={() => toggleTier(t.value)}
                    data-testid={`gift-tier-${t.value}`}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                      selectedTiers.includes(t.value) ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
